import { motion } from "framer-motion";
import { Film, Clapperboard, Wand2, Music2, Play, ArrowRight } from "lucide-react";

const SUB_SERVICES = [
  { icon: Clapperboard, title: "Reels & Shorts", desc: "Punchy, scroll-stopping vertical edits for Instagram, YouTube Shorts and TikTok." },
  { icon: Film, title: "Brand Films & Ads", desc: "Cinematic brand stories and TVCs with motion graphics, voiceover and pro colour grading." },
  { icon: Wand2, title: "Product & Promo Videos", desc: "Crisp product showcases, explainer videos and promotional cuts that convert viewers to buyers." },
  { icon: Music2, title: "Event & Wedding Edits", desc: "Cinematic event recap films, highlight reels and full wedding stories — emotion-led storytelling." },
];

export default function VideoEditing({ onQuoteClick }) {
  return (
    <section id="video-editing" className="py-24 bg-white relative overflow-hidden">
      <div className="absolute -top-32 right-0 h-[420px] w-[420px] rounded-full bg-brand-blue/5 blur-3xl" />
      <div className="absolute bottom-0 -left-20 h-[320px] w-[320px] rounded-full bg-brand-green/10 blur-3xl" />

      <div className="container-x relative grid lg:grid-cols-12 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-5"
        >
          <div className="overline text-brand-green">New · Now Offering</div>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl font-extrabold text-brand-ink leading-tight tracking-tight">
            Cinematic <span className="text-brand-blue">Video Editing</span> for brands that want to be seen.
          </h2>
          <p className="mt-5 text-gray-600 leading-relaxed">
            From scroll-stopping reels to polished brand films — our editing team turns raw footage into stories that drive engagement, trust and sales. Colour-graded, sound-mixed and ready to publish across every platform.
          </p>

          <ul className="mt-7 space-y-2.5 text-sm text-gray-700">
            <li className="flex gap-2"><span className="text-brand-green font-bold">✓</span> Adobe Premiere Pro · After Effects · DaVinci Resolve</li>
            <li className="flex gap-2"><span className="text-brand-green font-bold">✓</span> Custom motion graphics &amp; animated logos</li>
            <li className="flex gap-2"><span className="text-brand-green font-bold">✓</span> Royalty-free music &amp; pro voiceover sourcing</li>
            <li className="flex gap-2"><span className="text-brand-green font-bold">✓</span> Two rounds of revisions included</li>
          </ul>

          <div className="mt-8 flex flex-wrap gap-3">
            <button onClick={onQuoteClick} data-testid="video-quote-btn" className="btn-secondary">
              Request Video Quote <ArrowRight className="h-4 w-4" />
            </button>
            <a href="#contact" className="btn-outline">Talk to an editor</a>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-7"
        >
          <div className="relative rounded-[2rem] overflow-hidden border border-gray-200 shadow-xl">
            <img
              src="https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
              alt="Video editing studio"
              className="w-full h-[360px] sm:h-[420px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/80 via-brand-ink/20 to-transparent" />
            <button
              onClick={onQuoteClick}
              className="absolute inset-0 grid place-items-center group"
              aria-label="Play sample reel"
            >
              <span className="relative grid place-items-center">
                <span className="absolute inset-0 rounded-full bg-white/30 animate-ping" />
                <span className="relative h-20 w-20 grid place-items-center rounded-full bg-white text-brand-blue shadow-2xl group-hover:scale-110 transition">
                  <Play className="h-7 w-7 fill-brand-blue ml-1" />
                </span>
              </span>
            </button>
            <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between text-white">
              <div>
                <div className="overline text-brand-green">Sample Reel</div>
                <div className="font-display font-bold text-2xl mt-1">Brand storytelling, frame by frame.</div>
              </div>
              <div className="hidden sm:block glass rounded-2xl px-4 py-3 border border-white/40 text-brand-ink">
                <div className="text-[10px] uppercase tracking-widest font-bold text-gray-700">Avg. Turnaround</div>
                <div className="font-display font-bold">3–5 days</div>
              </div>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3">
            {SUB_SERVICES.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                data-testid={`video-sub-${i}`}
                className="rounded-2xl border border-gray-200 bg-white p-4 hover:border-brand-blue/40 hover:-translate-y-0.5 transition-all"
              >
                <div className="h-9 w-9 grid place-items-center rounded-lg bg-brand-blue/10 text-brand-blue">
                  <s.icon className="h-4 w-4" />
                </div>
                <h3 className="mt-3 font-display font-bold text-sm text-brand-ink">{s.title}</h3>
                <p className="mt-1.5 text-xs text-gray-600 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
